import { Label } from 'cpool-components/general/Label';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { CurrencyInput } from 'cpool-components/input/CurrencyInput';
import { NumberInput } from 'cpool-components/input/NumberInput';
import { SegmentedControl } from 'cpool-components/input/SegmentedControl';
import type { SegmentedControlOption } from 'cpool-components/input/SegmentedControl/types';
import { MEASURING_UNITS, REAL_ESTATE_TYPES, REAL_ESTATE_USAGES } from 'cpool-constants';
import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import type { ParseKeys } from 'i18next';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus } from 'react-icons/lu';

import { BuildingDescription } from './BuildingDescription';
import type { BuildingDescriptionStepId } from './BuildingDescription/types';
import RealEstateLoans from './RealEstateLoans';
import { FormDropdown } from '../shared/inputs/FormDropdown';
import { FormField } from '../shared/inputs/FormField';
import { useApplicantAttributionOptions } from '../shared/useDropdownOptions';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema';

type Props = {
    buildingDescriptionStep?: BuildingDescriptionStepId;
    currentApplicantAttribution: APPLICANT_ATTRIBUTION_BOTH_TYPE;
    hasBuildingDescription: boolean;
    hasSecondApplicant: boolean;
    isApplicantAttributionChangeDisabled: boolean;
    isBuildingDescriptionChangeDisabled: boolean;
    isChangeDisabled: boolean;
    onAddBuildingDescription: () => void;
    onApplicantAttributionChange: (value: APPLICANT_ATTRIBUTION_BOTH_TYPE) => boolean;
    onBuildingDescriptionStepChange?: (step: BuildingDescriptionStepId) => void;
    onRemoveBuildingDescription: () => void;
    onUsageTypeChange: (value: keyof typeof REAL_ESTATE_USAGES) => void;
    realEstateIndex: number;
    usageType: SchemaRealEstatesForm['immobilien'][number]['nutzungsart'];
};

const REAL_ESTATE_FIELD_LABEL_CLASS_NAME = 'h-8 w-[270px] shrink-0 justify-start';

export const RealEstateDetails: React.FC<Props> = ({
    buildingDescriptionStep,
    currentApplicantAttribution,
    hasBuildingDescription,
    hasSecondApplicant,
    isApplicantAttributionChangeDisabled,
    isBuildingDescriptionChangeDisabled,
    isChangeDisabled,
    onAddBuildingDescription,
    onApplicantAttributionChange,
    onBuildingDescriptionStepChange,
    onRemoveBuildingDescription,
    onUsageTypeChange,
    realEstateIndex,
    usageType,
}) => {
    const { t } = useTranslation();
    const { control } = useFormContext<SchemaRealEstatesForm>();
    const attributionOptions = useApplicantAttributionOptions();
    const usageOptions: SegmentedControlOption<keyof typeof REAL_ESTATE_USAGES>[] = [
        { label: t('REAL_ESTATE.USAGE.EIGENGENUTZT'), value: REAL_ESTATE_USAGES.EIGENGENUTZT },
        { label: t('REAL_ESTATE.USAGE.VERMIETET'), value: REAL_ESTATE_USAGES.VERMIETET },
        {
            label: t('REAL_ESTATE.USAGE.EIGENGENUTZT_UND_VERMIETET'),
            value: REAL_ESTATE_USAGES.EIGENGENUTZT_UND_VERMIETET,
        },
    ];
    const realEstateTypeOptions = Object.keys(REAL_ESTATE_TYPES).map((value) => ({
        label: t(`REAL_ESTATE.TYPE.${value}` as ParseKeys<'de'>),
        value,
    }));

    return (
        <div className="space-y-2 px-5 pb-5">
            {hasSecondApplicant && (
                <div className="flex items-start gap-2.5">
                    <Label className={REAL_ESTATE_FIELD_LABEL_CLASS_NAME}>{t('REAL_ESTATE.LABELS.OWNER')}</Label>
                    <SegmentedControl<APPLICANT_ATTRIBUTION_BOTH_TYPE>
                        id={`immobilien.${realEstateIndex}.gehoertZuAntragsteller`}
                        ariaLabel={t('REAL_ESTATE.LABELS.OWNER')}
                        options={attributionOptions}
                        value={currentApplicantAttribution}
                        onChange={onApplicantAttributionChange}
                        disabled={isApplicantAttributionChangeDisabled}
                    />
                </div>
            )}
            <div className="flex items-start gap-2.5">
                <Label className={REAL_ESTATE_FIELD_LABEL_CLASS_NAME}>{t('REAL_ESTATE.LABELS.USAGE_TYPE')}</Label>
                <SegmentedControl<keyof typeof REAL_ESTATE_USAGES>
                    id={`immobilien.${realEstateIndex}.nutzungsart`}
                    ariaLabel={t('REAL_ESTATE.LABELS.USAGE_TYPE')}
                    options={usageOptions}
                    value={usageType ?? null}
                    onChange={onUsageTypeChange}
                    disabled={isChangeDisabled}
                />
            </div>
            <FormDropdown
                control={control}
                id={`immobilien.${realEstateIndex}.immobilienart`}
                label={t('REAL_ESTATE.LABELS.TYPE')}
                labelClassName={REAL_ESTATE_FIELD_LABEL_CLASS_NAME}
                options={realEstateTypeOptions}
                searchable={false}
                disabled={isChangeDisabled}
            />
            <FormField<SchemaRealEstatesForm, number | null>
                control={control}
                name={`immobilien.${realEstateIndex}.wert`}
            >
                {({ error, onChange, value }) => (
                    <CurrencyInput
                        id={`immobilien.${realEstateIndex}.wert`}
                        label={t('REAL_ESTATE.LABELS.VALUE')}
                        labelClassName={REAL_ESTATE_FIELD_LABEL_CLASS_NAME}
                        className="w-full"
                        value={value}
                        onChange={onChange}
                        min={0}
                        disabled={isChangeDisabled}
                        error={error}
                    />
                )}
            </FormField>
            <FormField<SchemaRealEstatesForm, number | null>
                control={control}
                name={`immobilien.${realEstateIndex}.nebenkostenMonatlich`}
            >
                {({ error, onChange, value }) => (
                    <CurrencyInput
                        id={`immobilien.${realEstateIndex}.nebenkostenMonatlich`}
                        label={t('REAL_ESTATE.LABELS.ADDITIONAL_COSTS')}
                        labelClassName={REAL_ESTATE_FIELD_LABEL_CLASS_NAME}
                        className="w-full"
                        value={value}
                        onChange={onChange}
                        min={0}
                        disabled={isChangeDisabled}
                        error={error}
                    />
                )}
            </FormField>
            <FormField<SchemaRealEstatesForm, number | null>
                control={control}
                name={`immobilien.${realEstateIndex}.wohnflaeche`}
            >
                {({ error, onChange, value }) => (
                    <NumberInput
                        aria-label={`${t('REAL_ESTATE.LABELS.LIVING_SPACE')} (${MEASURING_UNITS.SQUARE_METERS})`}
                        id={`immobilien.${realEstateIndex}.wohnflaeche`}
                        label={t('REAL_ESTATE.LABELS.LIVING_SPACE')}
                        labelClassName={REAL_ESTATE_FIELD_LABEL_CLASS_NAME}
                        className="w-full"
                        value={value}
                        onChange={onChange}
                        min={0}
                        allowDecimals={false}
                        measuringUnit={MEASURING_UNITS.SQUARE_METERS}
                        disabled={isChangeDisabled}
                        error={error}
                    />
                )}
            </FormField>
            {usageType === REAL_ESTATE_USAGES.EIGENGENUTZT_UND_VERMIETET && (
                <FormField<SchemaRealEstatesForm, number | null>
                    control={control}
                    name={`immobilien.${realEstateIndex}.vermieteteWohnflaeche`}
                >
                    {({ error, onChange, value }) => (
                        <NumberInput
                            aria-label={`${t('REAL_ESTATE.LABELS.RENTED_LIVING_SPACE')} (${
                                MEASURING_UNITS.SQUARE_METERS
                            })`}
                            id={`immobilien.${realEstateIndex}.vermieteteWohnflaeche`}
                            label={t('REAL_ESTATE.LABELS.RENTED_LIVING_SPACE')}
                            labelClassName={REAL_ESTATE_FIELD_LABEL_CLASS_NAME}
                            className="w-full"
                            value={value}
                            onChange={onChange}
                            min={0}
                            allowDecimals={false}
                            measuringUnit={MEASURING_UNITS.SQUARE_METERS}
                            disabled={isChangeDisabled}
                            error={error}
                        />
                    )}
                </FormField>
            )}
            <FormField<SchemaRealEstatesForm, number | null>
                control={control}
                name={`immobilien.${realEstateIndex}.mieteinnahmenKaltMonatlich`}
            >
                {({ error, onChange, value }) => (
                    <CurrencyInput
                        id={`immobilien.${realEstateIndex}.mieteinnahmenKaltMonatlich`}
                        label={t('REAL_ESTATE.LABELS.RENTAL_INCOME_COLD')}
                        labelClassName={REAL_ESTATE_FIELD_LABEL_CLASS_NAME}
                        className="w-full"
                        value={value}
                        onChange={onChange}
                        min={0}
                        disabled={isChangeDisabled}
                        error={error}
                    />
                )}
            </FormField>
            <FormField<SchemaRealEstatesForm, number | null>
                control={control}
                name={`immobilien.${realEstateIndex}.mieteinnahmenWarmMonatlich`}
            >
                {({ error, onChange, value }) => (
                    <CurrencyInput
                        id={`immobilien.${realEstateIndex}.mieteinnahmenWarmMonatlich`}
                        label={t('REAL_ESTATE.LABELS.TOTAL_REVENUE')}
                        labelClassName={REAL_ESTATE_FIELD_LABEL_CLASS_NAME}
                        className="w-full"
                        value={value}
                        onChange={onChange}
                        min={0}
                        disabled={isChangeDisabled}
                        error={error}
                    />
                )}
            </FormField>
            <RealEstateLoans
                realEstateIndex={realEstateIndex}
                disabled={isChangeDisabled}
                additionalAction={
                    !hasBuildingDescription ? (
                        <Button
                            type="button"
                            variant={ButtonVariant.SECONDARY}
                            iconLeft={<LuPlus size={14} />}
                            onClick={onAddBuildingDescription}
                            disabled={isBuildingDescriptionChangeDisabled}
                        >
                            {t('REAL_ESTATE.BUILDING_DESCRIPTION.ADD')}
                        </Button>
                    ) : undefined
                }
            />
            {hasBuildingDescription && (
                <BuildingDescription
                    realEstateIndex={realEstateIndex}
                    disabled={isBuildingDescriptionChangeDisabled}
                    selectedStep={buildingDescriptionStep}
                    onSelectedStepChange={onBuildingDescriptionStepChange}
                    onRemove={onRemoveBuildingDescription}
                />
            )}
        </div>
    );
};
